import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Wallet, Flame, ChevronRight, Fuel, Wrench, Droplet, Receipt, Send, Calendar, Zap } from 'lucide-react';
import { formatMWK, formatHm } from './tokens';
import LogExpenseModal from './LogExpenseModal';
import CashoutModal from './CashoutModal';
import PawaPayHistory from '../../shared/PawaPayHistory';

const API = process.env.REACT_APP_BACKEND_URL;
const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'month', label: 'This month' },
];

const CATEGORY_ICON = {
  fuel: Fuel,
  maintenance: Wrench,
  wash: Droplet,
  other: Receipt,
};

/**
 * Screen 7 — Earnings.
 * Period toggle, big number, true-earnings deductions block, hourly/daily
 * breakdown bar, active quest mini-card, cashout, referral.
 */
const EarningsTab = ({ driverId }) => {
  const [period, setPeriod] = useState('today');
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [streak, setStreak] = useState(null);
  const [balance, setBalance] = useState(null);
  const [quests, setQuests] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [showCashout, setShowCashout] = useState(false);
  const [cashoutSchedule, setCashoutSchedule] = useState('instant');

  const fetchSummary = useCallback(async () => {
    if (!driverId) return;
    try {
      const r = await fetch(`${API}/api/driver/v2/${driverId}/earnings-summary?period=${period}`);
      const d = await r.json();
      if (d?.success) setSummary(d);
    } catch { /* ignore */ }
  }, [driverId, period]);

  const fetchAux = useCallback(async () => {
    if (!driverId) return;
    try {
      const [expRes, stRes, balRes, qRes] = await Promise.all([
        fetch(`${API}/api/driver/v2/${driverId}/expenses?limit=10`).then(r => r.json()).catch(() => null),
        fetch(`${API}/api/driver/v2/${driverId}/quest-streak`).then(r => r.json()).catch(() => null),
        fetch(`${API}/api/driver/v2/${driverId}/cashout-balance`).then(r => r.json()).catch(() => null),
        fetch(`${API}/api/driver/v2/${driverId}/quests`).then(r => r.json()).catch(() => null),
      ]);
      if (expRes?.success) setExpenses(expRes.items || []);
      if (stRes?.success) setStreak(stRes);
      if (balRes?.success) setBalance(balRes);
      if (qRes?.success) setQuests(qRes.items || []);
    } catch { /* ignore */ }
  }, [driverId]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchAux(); }, [fetchAux]);

  const chart = summary?.chart;
  const chartMax = chart?.values?.length ? Math.max(...chart.values, 1) : 1;

  return (
    <div className="absolute inset-0 pt-3 pb-24 overflow-y-auto bg-gray-50" data-testid="driver-earnings-tab-v2">
      <div className="px-4 pt-20 pb-4 space-y-3">
        {/* Period toggle */}
        <div className="bg-white rounded-2xl p-1 flex gap-1 shadow-sm" data-testid="earnings-period-toggle">
          {PERIODS.map(p => (
            <button
              key={p.id}
              type="button"
              onClick={() => setPeriod(p.id)}
              data-testid={`earnings-period-${p.id}`}
              className={`flex-1 h-9 rounded-xl text-[12px] font-semibold transition-colors ${
                period === p.id ? 'bg-emerald-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Big earnings number */}
        <div className="bg-white rounded-3xl p-5 shadow-sm">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">Gross earnings</p>
          <p className="text-[36px] font-bold text-gray-900 leading-tight tabular-nums" data-testid="earnings-gross">
            {formatMWK(summary?.gross || 0)}
          </p>
          <p className="text-[12px] text-gray-500 mt-1 tabular-nums">
            {summary?.trips || 0} trips
            {summary?.online_seconds != null ? ` · ${formatHm(summary.online_seconds)} online` : ''}
            {summary?.avg_per_trip ? ` · Avg ${formatMWK(summary.avg_per_trip)}/trip` : ''}
          </p>
        </div>

        {/* True earnings (gross − expenses) */}
        <div className="bg-white rounded-3xl p-5 shadow-sm" data-testid="earnings-true">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[13px] font-semibold text-gray-900">Your true earnings</p>
            <button
              type="button"
              onClick={() => setShowLog(true)}
              data-testid="earnings-log-expense-btn"
              className="inline-flex items-center gap-1 px-2.5 h-8 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold hover:bg-emerald-100"
            >
              <Plus className="w-3.5 h-3.5" /> Log fuel / maintenance
            </button>
          </div>
          <div className="space-y-1.5">
            <ExpenseRow label="Gross" value={summary?.gross || 0} positive />
            {Object.entries(summary?.expense_breakdown || {}).map(([cat, amt]) => {
              const Ic = CATEGORY_ICON[cat] || Receipt;
              return <ExpenseRow key={cat} icon={<Ic className="w-3.5 h-3.5" />} label={`${cat[0].toUpperCase()}${cat.slice(1)}`} value={amt} negative />;
            })}
          </div>
          <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between">
            <p className="text-[12px] text-gray-500 font-semibold">Net profit</p>
            <p className="text-[20px] font-bold text-emerald-700 tabular-nums" data-testid="earnings-net">
              {formatMWK(summary?.net || 0)}
            </p>
          </div>
          {expenses.length > 0 && (
            <details className="mt-3 group">
              <summary className="text-[11px] text-emerald-700 cursor-pointer font-semibold">Recent expenses ({expenses.length})</summary>
              <ul className="mt-2 space-y-1">
                {expenses.slice(0, 5).map(e => (
                  <li key={e.id} className="flex items-center justify-between text-[11px] text-gray-600">
                    <span className="capitalize">{e.category}{e.note ? ` · ${e.note}` : ''}</span>
                    <span className="tabular-nums">−{formatMWK(e.amount)}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        {/* Breakdown chart */}
        {chart && (
          <div className="bg-white rounded-3xl p-5 shadow-sm" data-testid="earnings-chart">
            <p className="text-[13px] font-semibold text-gray-900 mb-1">
              {period === 'today' ? 'Earnings by hour' : 'Earnings by day'}
            </p>
            <div className="flex items-end gap-1 h-24 mt-3">
              {chart.values.map((v, i) => {
                const pct = (v / chartMax) * 100;
                const isBest = chart.best_label && chart.labels[i] === chart.best_label && v > 0;
                return (
                  <div
                    key={`${chart.labels[i]}-${i}`}
                    className="flex-1 flex flex-col items-center justify-end"
                    title={`${chart.labels[i]}: ${formatMWK(v)}`}
                  >
                    <div
                      className={`w-full rounded-t-sm transition-[height] ${isBest ? 'bg-emerald-600' : 'bg-emerald-200'}`}
                      style={{ height: `${pct}%` }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between text-[9px] text-gray-400 mt-1 tabular-nums">
              {chart.labels.map((l, i) => (
                <span key={`l-${l}-${i}`} className={(period === 'today' && i % 3 !== 0) ? 'opacity-0' : ''}>{l}</span>
              ))}
            </div>
            {chart.best_label && (
              <p className="text-[12px] text-emerald-700 font-semibold mt-2">
                Your best {period === 'today' ? 'hour' : 'day'}: {chart.best_label}
              </p>
            )}
          </div>
        )}

        {/* Quest streak + active quest */}
        {streak && (
          <div
            className={`rounded-3xl p-4 shadow-sm border-2 ${streak.active ? 'border-orange-200 bg-orange-50' : 'border-gray-100 bg-white'}`}
            data-testid="earnings-streak-card"
          >
            <div className="flex items-center gap-2.5">
              <span className={`w-9 h-9 rounded-full flex items-center justify-center ${streak.active ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-400'}`}>
                <Flame className="w-4 h-4" />
              </span>
              <div className="flex-1">
                <p className="text-[13px] font-semibold text-gray-900">
                  {streak.active
                    ? `🔥 ${streak.uplift_pct}% streak active!`
                    : `Quest streak ${streak.completed_last_7_days}/${streak.threshold}`
                  }
                </p>
                <p className="text-[11px] text-gray-500">
                  {streak.active
                    ? `Your next quest pays +${streak.uplift_pct}% extra.`
                    : `Complete ${streak.threshold - streak.completed_last_7_days} more quest${streak.threshold - streak.completed_last_7_days === 1 ? '' : 's'} this week to unlock +20% bonus.`
                  }
                </p>
              </div>
            </div>
            <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-[width] duration-700 ${streak.active ? 'bg-gradient-to-r from-orange-400 to-orange-600' : 'bg-gray-400'}`}
                style={{ width: `${Math.min(100, (streak.completed_last_7_days / streak.threshold) * 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Active quest mini-cards */}
        {quests.length > 0 && (
          <div className="bg-white rounded-3xl p-4 shadow-sm" data-testid="earnings-quests-block">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[13px] font-semibold text-gray-900 flex items-center gap-1.5">
                <Zap className="w-4 h-4 text-amber-500" /> Active quests
              </p>
              <span className="text-[11px] text-gray-500">{quests.length}</span>
            </div>
            <ul className="space-y-2">
              {quests.slice(0, 3).map(q => {
                const pct = Math.min(100, Math.round((q.trip_count / Math.max(1, q.min_trips)) * 100));
                const done = !!q.completed_at;
                return (
                  <li key={q.id} className="bg-gray-50 rounded-2xl p-2.5">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p className="text-[12px] font-semibold text-gray-900 truncate flex-1">{q.title}</p>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${done ? 'bg-emerald-600 text-white' : 'bg-amber-500 text-white'}`}>
                        +{formatMWK(q.bonus_mwk)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                        <div className={`h-full ${done ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[10px] text-gray-500 tabular-nums">{q.trip_count}/{q.min_trips}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Cashout */}
        <div className="bg-emerald-600 rounded-3xl p-5 text-white shadow-lg shadow-emerald-600/20" data-testid="earnings-cashout">
          <p className="text-[11px] uppercase tracking-wider font-semibold opacity-80 flex items-center gap-1">
            <Wallet className="w-3.5 h-3.5" /> Available balance
          </p>
          <p className="text-[28px] font-bold mt-0.5 tabular-nums" data-testid="earnings-balance">
            {formatMWK(balance?.pending_balance || 0)}
          </p>
          <p className="text-[11px] opacity-80 mt-0.5 tabular-nums">
            Lifetime gross {formatMWK(balance?.lifetime_gross || 0)} · disbursed {formatMWK(balance?.lifetime_disbursed || 0)}
          </p>
          <div className="grid grid-cols-2 gap-2 mt-4">
            <button
              type="button"
              onClick={() => { setCashoutSchedule('instant'); setShowCashout(true); }}
              disabled={(balance?.pending_balance || 0) <= 0}
              data-testid="earnings-cashout-btn"
              className="h-12 rounded-2xl bg-white text-emerald-700 font-semibold text-[12px] inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
            >
              <Send className="w-4 h-4" /> Cash out now
            </button>
            <button
              type="button"
              onClick={() => { setCashoutSchedule('weekly'); setShowCashout(true); }}
              data-testid="earnings-schedule-btn"
              className="h-12 rounded-2xl bg-emerald-700 text-white font-semibold text-[12px] inline-flex items-center justify-center gap-1.5 hover:bg-emerald-800"
            >
              <Calendar className="w-4 h-4" /> Schedule weekly
            </button>
          </div>
          <p className="text-[10px] opacity-70 mt-2 text-center">Instant cashout · No fee</p>
        </div>

        {/* Cash-out history */}
        <div className="bg-gray-50 rounded-3xl p-4" data-testid="earnings-payout-history">
          <PawaPayHistory driverId={driverId} title="Recent cash-outs" />
        </div>

        {/* Referral card */}
        <div className="bg-white rounded-3xl p-4 shadow-sm flex items-center gap-3" data-testid="earnings-referral">
          <span className="w-12 h-12 rounded-2xl bg-rose-100 text-rose-600 flex items-center justify-center text-lg">🤝</span>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-gray-900">Refer a driver — earn K10,000</p>
            <p className="text-[11px] text-gray-500">Bonus paid when they complete 5 trips</p>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </div>
      </div>

      {showLog && (
        <LogExpenseModal
          driverId={driverId}
          onClose={() => setShowLog(false)}
          onSaved={() => { setShowLog(false); fetchSummary(); fetchAux(); }}
        />
      )}
      {showCashout && (
        <CashoutModal
          driverId={driverId}
          balance={balance?.pending_balance || 0}
          defaultSchedule={cashoutSchedule}
          onClose={() => setShowCashout(false)}
          onSaved={() => { setShowCashout(false); fetchAux(); }}
        />
      )}
    </div>
  );
};

const ExpenseRow = ({ icon, label, value, positive, negative }) => (
  <div className="flex items-center justify-between text-[12px]">
    <span className="text-gray-600 inline-flex items-center gap-1.5">{icon} {label}</span>
    <span className={`tabular-nums font-semibold ${negative ? 'text-rose-600' : positive ? 'text-gray-900' : 'text-gray-700'}`}>
      {negative ? '−' : ''}{formatMWK(value)}
    </span>
  </div>
);

export default EarningsTab;
